import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { Account, AccountInput, PipelineStages, TimelineItem } from '../shared/types'

export const useAccounts = () => useQuery({ queryKey: ['accounts'], queryFn: () => api.get<Account[]>('/api/v1/accounts') })
export const useAccount = (id: string) => useQuery({ queryKey: ['accounts', id], queryFn: () => api.get<Account>(`/api/v1/accounts/${id}`), enabled: !!id })
export const useStages = (productId: string | undefined) =>
  useQuery({
    queryKey: ['pipeline-stages', productId ?? ''],
    queryFn: () => api.get<PipelineStages>('/api/v1/pipeline-stages', { product_id: productId }),
    staleTime: 5 * 60_000,
  })

const PAGE = 30
/** Zaman çizelgesi imleçli sayfalanır: sonraki sayfa son kalemin zamanından öncesidir. */
export const useTimeline = (id: string, kinds: string[]) =>
  useInfiniteQuery({
    queryKey: ['accounts', id, 'timeline', kinds],
    initialPageParam: '',
    queryFn: ({ pageParam }) => api.get<TimelineItem[]>(`/api/v1/accounts/${id}/timeline`, { kinds: kinds.join(','), before: pageParam, limit: PAGE }),
    getNextPageParam: (last) => (last.length === PAGE ? last[last.length - 1]?.at : undefined),
    enabled: !!id,
  })

export function useCreateAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AccountInput) => api.post<Account>('/api/v1/accounts', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['accounts'] }),
  })
}

export function useUpdateAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<AccountInput>) => api.put<Account>(`/api/v1/accounts/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      void qc.invalidateQueries({ queryKey: ['accounts', id] })
    },
  })
}

export function useLogAccountEmail(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mail_id: string) => api.post(`/api/v1/accounts/${id}/emails`, { mail_id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['accounts', id, 'timeline'] }),
  })
}

export function useExportAccounts() {
  return useMutation({
    mutationFn: (format: 'csv' | 'xlsx') => api.download(`/api/v1/accounts/export.${format}`, `accounts.${format}`),
  })
}
